'use client';

import { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import BrandHeader from '@/components/common/BrandHeader';
import ProgressIndicator from '@/components/common/ProgressIndicator';
import StepContact from './StepContact';
import StepPressurePoints from './StepPressurePoints';
import StepAreaAssessment from './StepAreaAssessment';
import StepSupportPreferences from './StepSupportPreferences';

export interface ContactData {
  name: string;
  parishCouncil: string;
  municipality: string;
  position: string;
  email: string;
  phone: string;
  employeeCount: string;
  voterCount: string;
}

export interface PressureData {
  mainChallenges: string[];
  operationalFrequencies: string[];
}

export interface AreaScore {
  adminOrg: number;
  financialMgmt: number;
  humanResources: number;
  publicProcurement: number;
}

export interface SupportData {
  preference: string;
  additionalNotes: string;
}

export interface FormData {
  contact: ContactData;
  pressure: PressureData;
  areaScores: AreaScore;
  support: SupportData;
  totalScore: number;
}

const initialFormData: FormData = {
  contact: {
    name: '',
    parishCouncil: '',
    municipality: '',
    position: '',
    email: '',
    phone: '',
    employeeCount: '',
    voterCount: '',
  },
  pressure: {
    mainChallenges: [],
    operationalFrequencies: [],
  },
  areaScores: {
    adminOrg: 0,
    financialMgmt: 0,
    humanResources: 0,
    publicProcurement: 0,
  },
  support: {
    preference: '',
    additionalNotes: '',
  },
  totalScore: 0,
};

function calculateScore(data: FormData): number {
  let score = 0;
  score += data.pressure.mainChallenges.length;
  score += data.pressure.operationalFrequencies.length;
  const areaTotal =
    data.areaScores.adminOrg +
    data.areaScores.financialMgmt +
    data.areaScores.humanResources +
    data.areaScores.publicProcurement;
  score += areaTotal;
  return Math.min(score, 16);
}

function isContactValid(contact: ContactData): boolean {
  return !!(
    contact.name.trim() &&
    contact.parishCouncil.trim() &&
    contact.municipality.trim() &&
    contact.position.trim() &&
    contact.email.trim() &&
    contact.phone.trim() &&
    contact.employeeCount &&
    contact.voterCount
  );
}

function isPressureValid(pressure: PressureData): boolean {
  return pressure.mainChallenges.length > 0 && pressure.operationalFrequencies.length > 0;
}

function isAreaValid(areaScores: AreaScore): boolean {
  return (
    areaScores.adminOrg > 0 &&
    areaScores.financialMgmt > 0 &&
    areaScores.humanResources > 0 &&
    areaScores.publicProcurement > 0
  );
}

function isSupportValid(support: SupportData): boolean {
  return !!support.preference;
}

export default function DiagnosticFormInteractive() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(2);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);

  const updateContact = useCallback((contact: ContactData) => {
    setFormData((prev) => ({ ...prev, contact }));
  }, []);

  const updatePressure = useCallback((pressure: PressureData) => {
    setFormData((prev) => ({ ...prev, pressure }));
  }, []);

  const updateAreaScores = useCallback((areaScores: AreaScore) => {
    setFormData((prev) => ({ ...prev, areaScores }));
  }, []);

  const updateSupport = useCallback((support: SupportData) => {
    setFormData((prev) => ({ ...prev, support }));
  }, []);

  const canProceed = useCallback((): boolean => {
    const internalStep = currentStep - 1;
    switch (internalStep) {
      case 1: return isContactValid(formData.contact);
      case 2: return isPressureValid(formData.pressure);
      case 3: return isAreaValid(formData.areaScores);
      case 4: return isSupportValid(formData.support);
      default: return false;
    }
  }, [currentStep, formData]);

  const handleNext = useCallback(async () => {
    if (currentStep < 5) {
      setCurrentStep((s) => s + 1);
    } else {
      setIsLoading(true);
      let score = calculateScore(formData);
      const updatedData = { ...formData, totalScore: score };
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('diagnosticResults', JSON.stringify(updatedData));
      }
      await new Promise((r) => setTimeout(r, 800));
      router.push('/results-and-recommendations');
    }
  }, [currentStep, formData, router]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 2) {
      setCurrentStep((s) => s - 1);
    } else {
      router.push('/diagnostic-landing-page');
    }
  }, [currentStep, router]);

  const internalStep = currentStep - 1;
  const isLastStep = currentStep === 5;

  const stepLabels: Record<number, string> = {
    2: 'Passo 1 de 4 — Informações de Contacto',
    3: 'Passo 2 de 4 — Pontos de Pressão',
    4: 'Passo 3 de 4 — Avaliação por Área',
    5: 'Passo 4 de 4 — Preferências de Apoio',
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F9F9F9' }}>
      <BrandHeader activePage="form" />
      <ProgressIndicator currentStep={2} completedSteps={[1]} />

      {/* Sub-step progress */}
      <div className="w-full bg-white" style={{ borderBottom: '1px solid #F0F0F0' }}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span
              className="text-gray-500 text-sm"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {stepLabels[currentStep]}
            </span>
            <span
              className="font-medium text-sm"
              style={{ fontFamily: 'JetBrains Mono, monospace', color: '#009688' }}
            >
              {Math.round(((internalStep - 1) / 4) * 100)}%
            </span>
          </div>
          <div className="h-1 bg-gray-100 overflow-hidden" style={{ borderRadius: '0' }}>
            <div
              className="h-full transition-all duration-300"
              style={{ width: `${((internalStep - 1) / 4) * 100}%`, backgroundColor: '#009688' }}
            />
          </div>
          {/* Step indicators */}
          <div className="hidden md:flex items-center justify-between mt-4">
            {['Contacto', 'Pressões', 'Avaliação', 'Apoio'].map((label, idx) => {
              const stepNum = idx + 2;
              const done = currentStep > stepNum;
              const active = currentStep === stepNum;
              return (
                <div key={label} className="flex items-center gap-1.5">
                  <div
                    className="w-5 h-5 flex items-center justify-center text-xs font-medium transition-all duration-200"
                    style={{
                      borderRadius: '5px',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: '0.65rem',
                      backgroundColor: done ? '#2E7D32' : active ? '#009688' : '#F5F5F5',
                      color: done || active ? 'white' : '#888',
                      border: done || active ? 'none' : '1px solid #E0E0E0',
                    }}
                  >
                    {done ? '✓' : idx + 1}
                  </div>
                  <span
                    className="text-xs hidden lg:inline"
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      color: active ? '#009688' : done ? '#2E7D32' : '#888',
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 md:px-6 py-8">
        {currentStep === 2 && (
          <StepContact data={formData.contact} onChange={updateContact} />
        )}
        {currentStep === 3 && (
          <StepPressurePoints data={formData.pressure} onChange={updatePressure} />
        )}
        {currentStep === 4 && (
          <StepAreaAssessment data={formData.areaScores} onChange={updateAreaScores} />
        )}
        {currentStep === 5 && (
          <StepSupportPreferences data={formData.support} onChange={updateSupport} />
        )}
      </main>

      {/* Navigation */}
      <div
        className="w-full bg-white"
        style={{ borderTop: '1px solid #F0F0F0', boxShadow: '0 -1px 4px rgba(0,0,0,0.06)' }}
      >
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            {/* Previous button */}
            <button
              type="button"
              onClick={handlePrevious}
              disabled={isLoading}
              className="inline-flex items-center gap-2 h-11 px-6 font-semibold text-sm transition-all duration-250 hover:border-primary hover:text-primary active:scale-97 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                borderRadius: '5px',
                fontFamily: 'DM Sans, sans-serif',
                border: '2px solid #E0E0E0',
                backgroundColor: 'white',
                color: '#555',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              <span className="hidden sm:inline">Anterior</span>
            </button>

            {/* Step dots */}
            <div className="flex items-center gap-2" aria-hidden="true">
              {[2, 3, 4, 5].map((step) => (
                <div
                  key={step}
                  className="transition-all duration-250"
                  style={{
                    borderRadius: '2px',
                    width: step === currentStep ? '24px' : '8px',
                    height: '8px',
                    backgroundColor: step === currentStep ? '#009688' : step < currentStep ? '#2E7D32' : '#E0E0E0',
                  }}
                />
              ))}
            </div>

            {/* Next button */}
            <button
              type="button"
              onClick={handleNext}
              disabled={!canProceed() || isLoading}
              className="inline-flex items-center gap-2 h-11 px-6 font-semibold text-sm transition-all duration-250 active:scale-97 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                borderRadius: '5px',
                fontFamily: 'DM Sans, sans-serif',
                backgroundColor: canProceed() && !isLoading ? '#009688' : '#F5F5F5',
                color: canProceed() && !isLoading ? 'white' : '#999',
                border: canProceed() && !isLoading ? '2px solid #009688' : '2px solid transparent',
              }}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>A processar...</span>
                </>
              ) : (
                <>
                  <span>{isLastStep ? 'Ver Resultados' : 'Continuar'}</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </>
              )}
            </button>
          </div>

          {/* Validation hint */}
          {!canProceed() && !isLoading && (
            <p
              className="mt-3 text-center text-xs"
              role="alert"
              aria-live="polite"
              style={{ fontFamily: 'DM Sans, sans-serif', color: '#C62828' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline mr-1 align-text-bottom" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Por favor, preencha todos os campos obrigatórios para continuar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}